import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useWebPhone, WebPhoneCredentials, ConnectionStatus, CallStatus, CallSession, RTCStats } from '../hooks/useWebPhone';
import api from '../services/api';

interface WebPhoneContextType {
  isWindowOpen: boolean;
  isMinimized: boolean;
  connectionStatus: ConnectionStatus;
  callStatus: CallStatus;
  currentCall: CallSession | null;
  openWindow: () => void;
  closeWindow: () => void;
  minimizeWindow: () => void;
  restoreWindow: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  openPictureInPicture: () => Promise<void>;
  // Forward all webphone functions
  makeCall: (number: string) => void;
  answerCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  isMuted: boolean;
  isOnHold: boolean;
  dialedNumber: string;
  addDigit: (digit: string) => void;
  clearNumber: () => void;
  errorMessage: string | null;
  // Audio device management
  audioDevices: { inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] };
  selectedSpeaker: string;
  selectedMicrophone: string;
  volume: number;
  changeSpeaker: (deviceId: string) => void;
  changeMicrophone: (deviceId: string) => void;
  changeVolume: (volume: number) => void;
  enumerateDevices: () => Promise<void>;
  testSpeaker: () => Promise<void>;
  // RTC Statistics
  rtcStats: RTCStats | null;
  // Event Logs
  eventLogs: Array<{ timestamp: string; message: string; type: 'info' | 'success' | 'error' | 'warning' }>;
  // Debug Mode
  debugMode: boolean;
  toggleDebugMode: () => void;
}

const WebPhoneContext = createContext<WebPhoneContextType | undefined>(undefined);

export const useWebPhoneContext = () => {
  const context = useContext(WebPhoneContext);
  if (!context) {
    throw new Error('useWebPhoneContext must be used within WebPhoneProvider');
  }
  return context;
};

export const WebPhoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [credentials, setCredentials] = useState<WebPhoneCredentials | null>(null);
  const hasAutoConnectedRef = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log('[WebPhoneContext] State changed - isWindowOpen:', isWindowOpen, 'isMinimized:', isMinimized);
  }, [isWindowOpen, isMinimized]);

  const webphone = useWebPhone();

  // Fetch credentials from API
  const fetchCredentials = useCallback(async () => {
    try {
      console.log('[WebPhone] Fetching credentials from API...');
      console.log('[WebPhone] API endpoint: /endpoints/webphone-credentials');

      // Check if we have an auth token before making the request
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      console.log('[WebPhone] Auth token present:', !!token);
      if (token) {
        console.log('[WebPhone] Token preview:', token.substring(0, 30) + '...');
      } else {
        console.error('[WebPhone] NO TOKEN FOUND - API call will likely fail or hang');
      }

      console.log('[WebPhone] About to call api.get...');
      const response = await api.get('/endpoints/webphone-credentials');
      console.log('[WebPhone] API call returned, checking response...');
      console.log('[WebPhone] Response status:', response.status);
      console.log('[WebPhone] Response data:', response.data);
      if (response.data.success) {
        console.log('[WebPhone] Success=true, extracting credentials...');
        setCredentials(response.data.data);
        console.log('[WebPhone] Credentials fetched successfully:', response.data.data);
        return response.data.data;
      }
      console.error('[WebPhone] Success=false, throwing error');
      throw new Error(response.data.message || 'Failed to fetch credentials');
    } catch (error: any) {
      console.error('[WebPhone] CATCH BLOCK - Failed to fetch credentials:', error);
      console.error('[WebPhone] Error name:', error.name);
      console.error('[WebPhone] Error message:', error.message);
      console.error('[WebPhone] Error response:', error.response);
      console.error('[WebPhone] Error details:', error.response?.data || error.message);
      throw error;
    }
  }, []);

  // Connect to WebRTC
  const connect = useCallback(async () => {
    try {
      console.log('[WebPhone] Connect called, current status:', webphone.connectionStatus);
      let creds = credentials;
      if (!creds) {
        console.log('[WebPhone] No cached credentials, fetching from API...');
        creds = await fetchCredentials();
        console.log('[WebPhone] fetchCredentials() returned:', creds ? 'GOT CREDENTIALS' : 'NULL/UNDEFINED');
      } else {
        console.log('[WebPhone] Using cached credentials');
      }
      if (creds) {
        console.log('[WebPhone] Connecting to SBC with credentials:', { username: creds.username, sbcServers: creds.sbcServers });
        await webphone.connect(creds);
        console.log('[WebPhone] webphone.connect() completed');
      } else {
        console.error('[WebPhone] No credentials available - creds is null/undefined');
      }
    } catch (error) {
      console.error('[WebPhone] CONNECT CATCH BLOCK - Connect failed:', error);
      console.error('[WebPhone] Error details:', error);
    }
  }, [credentials, fetchCredentials, webphone]);

  // Connect when window opens
  useEffect(() => {
    if (isWindowOpen && webphone.connectionStatus === 'disconnected' && !hasAutoConnectedRef.current) {
      console.log('[WebPhone] Window opened - connecting...');
      hasAutoConnectedRef.current = true;
      connect();
    } else if (!isWindowOpen) {
      // Reset the flag when window closes so next open will connect
      hasAutoConnectedRef.current = false;
    }
  }, [isWindowOpen, webphone.connectionStatus, connect]);

  const openWindow = useCallback(() => {
    console.log('[WebPhoneContext] openWindow called');
    setIsWindowOpen(true);
    setIsMinimized(false);
  }, []);

  const closeWindow = useCallback(() => {
    console.log('[WebPhoneContext] closeWindow called');
    setIsWindowOpen(false);
    setIsMinimized(false);
    webphone.disconnect();
  }, [webphone]);

  const minimizeWindow = useCallback(() => {
    console.log('[WebPhoneContext] minimizeWindow called');
    setIsMinimized(true);
  }, []);

  const restoreWindow = useCallback(() => {
    console.log('[WebPhoneContext] restoreWindow called');
    setIsMinimized(false);
  }, []);

  const openPictureInPicture = useCallback(async () => {
    try {
      // For now, just use a regular popup window
      // Document PiP requires complex React rendering into separate document
      // which needs more setup. Regular popup works well.
      const width = 340;
      const height = 600;
      const left = window.screen.width - width - 20;
      const top = 80;

      const popup = window.open(
        '/webphone-popup',
        'WebPhone',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
      );

      if (popup) {
        console.log('[WebPhone] Popup window opened');
      } else {
        alert('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      console.error('[WebPhone] Failed to open popup:', error);
      alert('Failed to open WebPhone window.');
    }
  }, []);

  const value: WebPhoneContextType = {
    isWindowOpen,
    isMinimized,
    connectionStatus: webphone.connectionStatus,
    callStatus: webphone.callStatus,
    currentCall: webphone.currentCall,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    connect,
    disconnect: webphone.disconnect,
    openPictureInPicture,
    makeCall: webphone.makeCall,
    answerCall: webphone.answerCall,
    hangUp: webphone.hangUp,
    toggleMute: webphone.toggleMute,
    toggleHold: webphone.toggleHold,
    isMuted: webphone.isMuted,
    isOnHold: webphone.isOnHold,
    dialedNumber: webphone.dialedNumber,
    addDigit: webphone.addDigit,
    clearNumber: webphone.clearNumber,
    errorMessage: webphone.errorMessage,
    audioDevices: webphone.audioDevices,
    selectedSpeaker: webphone.selectedSpeaker,
    selectedMicrophone: webphone.selectedMicrophone,
    volume: webphone.volume,
    changeSpeaker: webphone.changeSpeaker,
    changeMicrophone: webphone.changeMicrophone,
    changeVolume: webphone.changeVolume,
    enumerateDevices: webphone.enumerateDevices,
    testSpeaker: webphone.testSpeaker,
    rtcStats: webphone.rtcStats,
    eventLogs: webphone.eventLogs,
    debugMode: webphone.debugMode,
    toggleDebugMode: webphone.toggleDebugMode,
  };

  return <WebPhoneContext.Provider value={value}>{children}</WebPhoneContext.Provider>;
};
